/**
 * Application wiring.
 *
 * Owns the object graph and nothing else: parameters in, subsystems
 * constructed, loop started. Subsystems register their own HUD lines and debug
 * toggles rather than being enumerated here, so later phases add content
 * without touching this file.
 */

import * as THREE from 'three';
import { Autopilot } from './core/autopilot';
import { CameraRig } from './core/camera-rig';
import { PlayerController } from './core/player-controller';
import { Loop } from './core/loop';
import { hasParam, parseParams, serializeParams, type AppParams } from './core/params';
import { FrameTimer } from './debug/frame-timer';
import { formatCount, Hud, HudOrder, readHeapMb } from './debug/hud';
import { DebugPanel } from './debug/panel';
import { Renderer } from './render/renderer';
import { CubeScene } from './scene/cube';
import { ChunkStreamer } from './world/chunk-streamer';
import {
  buildingDrawsSinceReset,
  deckDrawsSinceReset,
  propDrawsSinceReset,
  resetBuildingDraws,
  resetDeckDraws,
  resetPropDraws,
  resetRiverDraws,
  resetRoadDraws,
  resetStreetDraws,
  resetWallDraws,
  resetWaterDraws,
  riverDrawsSinceReset,
  roadDrawsSinceReset,
  streetDrawsSinceReset,
  wallDrawsSinceReset,
  waterDrawsSinceReset,
} from './world/chunk-mesh';
import { sampleHeight } from './world/height-field';
import { createWorldCollision } from './world/collision';
import { InteriorOverlay } from './world/interior-overlay';
import { NpcOverlay, npcDrawsSinceReset, resetNpcDraws } from './world/npc-overlay';
import { WorldMap } from './debug/world-map';
import { TravelMap } from './ui/travel-map';
import { Atmosphere } from './sky/atmosphere';
import {
  moonDrawsSinceReset,
  resetSkyDraws,
  skyDrawsSinceReset,
  starDrawsSinceReset,
} from './sky/sky-dome';

declare global {
  interface Window {
    /**
     * Set once the first frames have actually been rendered. The screenshot
     * harness waits on this instead of sleeping, so captures can never race the
     * first frame.
     */
    __worldReady?: boolean;
    /**
     * Set to `false` by a harness BEFORE the document runs to hold the
     * autopilot at `?pos=` until it says otherwise; set back to `true` to
     * release it. Absent -- the normal case, including a human opening a
     * `?fly=` URL -- means released.
     *
     * `__worldReady` alone is not enough for the soak, and Phase 5 found out
     * why. Readiness is observed by polling from Node, and the main thread
     * stalls for over a second at a time building meshes under a software
     * rasteriser, so the poll lands late and the flight has already covered
     * hundreds of metres by the time the baseline is taken. Everything the soak
     * says "at the start" is then a claim about a square nobody chose.
     */
    __flightReleased?: boolean;
    __app?: App;
  }
}

/** Frames that must render before the app declares itself ready. */
const READY_FRAME_COUNT = 2;

/**
 * Metres above raw `sampleHeight` a teleport lands at. Matches the constructor's
 * own default-spawn offset (`DEFAULT_PARAMS.pos.y`) for the same reason: a
 * walking player's floor snap (`PlayerController.update`) corrects the exact
 * height from here regardless, and a flying camera just needs clearance above
 * whatever the ungraded heightfield says at the target.
 */
const TELEPORT_HEIGHT_OFFSET = 2.5;

export class App {
  readonly params: AppParams;

  private readonly renderer: Renderer;
  private readonly scene = new THREE.Scene();
  private readonly rig: CameraRig;
  private readonly cube: CubeScene;
  private readonly streamer: ChunkStreamer;
  private readonly autopilot: Autopilot;
  private readonly hud: Hud;
  private readonly panel: DebugPanel;
  private readonly frameTimer = new FrameTimer();
  private readonly loop: Loop;
  private readonly player: PlayerController | null;
  private readonly interiors: InteriorOverlay;
  private readonly npcs: NpcOverlay;
  private readonly worldMap: WorldMap;
  /**
   * Full-screen interactive travel map: click a settlement to teleport there.
   * Reuses `WorldMap`'s political sampler at a wider scale -- see
   * `src/ui/travel-map.ts`. Toggled with `N`; `M` toggles the corner minimap.
   */
  private readonly travelMap: TravelMap;
  /** Phase 10: the sun, the moon, and everything that follows from where they are. */
  private readonly atmosphere: Atmosphere;

  private renderedFrames = 0;
  /**
   * Water submeshes actually rasterised in the last frame.
   *
   * Not a cosmetic HUD line: the soak fails if this never rises above zero, so
   * a Phase 3a whose flight happens to miss the sea is a failure rather than a
   * green run that proved nothing. See `chunk-mesh.ts`.
   */
  private waterDrawCalls = 0;
  /**
   * Terrain meshes carrying river carving actually rasterised in the last
   * frame. Same role as `waterDrawCalls`, and rivers need it more: a river is
   * not its own mesh, so nothing else can tell "no river nearby" apart from
   * "carving stopped working". See `chunk-mesh.ts`.
   */
  private riverDrawCalls = 0;
  /**
   * Terrain meshes carrying road surfacing actually rasterised in the last
   * frame. Same role as `riverDrawCalls`, and needed for the same reason: a
   * road is not its own mesh either. See `chunk-mesh.ts`.
   */
  private roadDrawCalls = 0;
  /**
   * Terrain meshes carrying Phase 4b street surfacing actually rasterised in the
   * last frame. The third of the same guard; a village is a handful of nodes in
   * a 4 km region, so this is the one most likely to be quietly zero.
   */
  private streetDrawCalls = 0;
  /**
   * Phase 5 deck submeshes actually rasterised in the last frame.
   *
   * The first of these counters since Phase 3a's water that measures a mesh of
   * its own rather than a feature baked into the terrain -- which is exactly why
   * a deck is the first thing since Phase 2b that can move the draw-call budget.
   */
  private deckDrawCalls = 0;

  /**
   * Phase 6 building submeshes actually rasterised in the last frame.
   *
   * The second mesh of its own after the deck, and the sparsest thing in the
   * world: it is zero on almost every frame of a flight, and non-zero only over
   * a settlement. That is what makes it worth measuring separately rather than
   * folding into `deckDrawCalls`.
   */
  private buildingDrawCalls = 0;
  private wallDrawCalls = 0;
  private propDrawCalls = 0;
  private npcDrawCalls = 0;
  /**
   * Phase 10. The sky dome, the star field and the moon disc, actually
   * rasterised in the last frame.
   *
   * `skyDrawCalls` is the guard whose absence would be hardest to notice: the
   * renderer's clear colour is a dark blue that could pass for a night sky, so
   * a dome that never draws looks like a working night rather than a bug.
   * `starDrawCalls` carries a second claim -- the stars are invisible above the
   * fade threshold, so a non-zero count also proves the flight reached a night.
   */
  private skyDrawCalls = 0;
  private starDrawCalls = 0;
  private moonDrawCalls = 0;

  constructor(canvas: HTMLCanvasElement, hudElement: HTMLElement, search: string) {
    this.params = parseParams(search);

    this.renderer = new Renderer(canvas);
    // With terrain, one fixed default camera height is underground on one seed
    // and in the clouds on the next, so the DEFAULT Y is read as metres above
    // the ground. An explicit `?pos=` is always absolute -- a URL has to mean
    // exactly one thing or the whole screenshot harness stops reproducing.
    const start = hasParam(search, 'pos')
      ? this.params.pos
      : {
          ...this.params.pos,
          y: sampleHeight(this.params.pos.x, this.params.pos.z, this.params.seedHash) + this.params.pos.y,
        };
    this.rig = new CameraRig(start, this.params.look);
    this.player = this.params.walk
      ? new PlayerController(this.rig, createWorldCollision(this.params.seedHash))
      : null;
    this.cube = new CubeScene(this.params.seedHash);
    this.hud = new Hud(hudElement, { visible: this.params.hud });
    this.panel = new DebugPanel({ visible: this.params.panel, title: 'Infinite World' });
    // Phase Politics P4. Off by default, and deliberately not part of any
    // canonical screenshot -- see `src/debug/world-map.ts` and `?map=` in
    // `src/core/params.ts`.
    this.worldMap = new WorldMap(this.params.seedHash, { visible: this.params.map });
    document.body.appendChild(this.worldMap.element);
    this.travelMap = new TravelMap(this.params.seedHash, {
      onTeleport: (x, z) => this.teleportTo(x, z),
    });
    document.body.appendChild(this.travelMap.element);

    // All world generation happens in workers owned by the streamer; the main
    // thread only builds meshes from the payloads they transfer back.
    this.streamer = new ChunkStreamer({
      worldSeed: this.params.seedHash,
      onSceneChanged: () => this.renderer.invalidateMaterials(),
    });
    this.autopilot = new Autopilot(this.params.fly, this.params.flyLeg);
    this.interiors = new InteriorOverlay(this.params.seedHash);
    this.npcs = new NpcOverlay(this.params.seedHash);

    // Phase 10. Replaces the Phase 0 placeholder rig outright: the sun and moon
    // now move, driven by `Loop.simTime` so a frozen loop holds them still and
    // `?time=` seeks them. See `src/sky/atmosphere.ts`.
    this.atmosphere = new Atmosphere({ todAtZero: this.params.tod });
    // Assigned once. `Atmosphere` mutates the instance every frame, which is
    // what lets it own the fog without owning a reference to the scene.
    this.scene.fog = this.atmosphere.fog;

    this.scene.add(
      this.cube.root,
      this.streamer.root,
      this.interiors.root,
      this.npcs.root,
      this.atmosphere.root,
    );

    this.renderer.setWireframe(this.params.wireframe);

    // `?time=` seeks to an absolute simulation tick, so the pose in a
    // screenshot is a function of the URL rather than of elapsed wall time.
    this.loop = new Loop(
      (_dt, tick) => {
        this.cube.update(tick * (1 / 60));
        // NPCs step from the FIXED update only, never from `renderFrame`. A
        // paused Loop (canonical screenshots force `freeze=1`) runs zero
        // fixed updates, so every capture sees only a crowd's tick-pure
        // birth state -- see `npcs.ts`'s header and the Phase 9 entry in
        // `PROGRESS.md`.
        if (this.params.npc) {
          const p = this.rig.position;
          this.npcs.step(tick, p.x, p.z);
        }
      },
      (_alpha, wallDt) => this.renderFrame(wallDt),
      {
        startTick: Math.round(this.params.time * 60),
        paused: this.params.freeze,
      },
    );
    // Put the scene into its start state before the first frame is drawn.
    this.cube.update(this.loop.simTime);

    this.registerHudLines();
    this.buildPanel();
    this.cube.registerDebug(this.hud, this.panel);
    // Replaces the placeholder `chunks` and `worker queue` lines registered
    // above, by label. See `Hud.register`.
    this.streamer.registerDebug(this.hud, this.panel);
    this.atmosphere.registerDebug(this.hud, this.panel);

    if (this.player === null) this.rig.attach(canvas);
    else this.player.attach(canvas);
    window.addEventListener('resize', this.onResize);
    window.addEventListener('keydown', this.onKeyDown);
    this.onResize();
  }

  start(): void {
    this.loop.start();
  }

  dispose(): void {
    this.loop.stop();
    this.rig.detach();
    this.player?.detach();
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('keydown', this.onKeyDown);
    this.panel.destroy();
    this.cube.dispose();
    this.streamer.dispose();
    this.interiors.dispose();
    this.npcs.dispose();
    this.worldMap.dispose();
    this.travelMap.dispose();
    this.atmosphere.dispose();
    this.renderer.dispose();
  }

  /** Current state as URL parameters, for the panel's copy-link button. */
  currentParams(): AppParams {
    return {
      ...this.params,
      pos: this.rig.position,
      look: this.rig.look,
      freeze: this.loop.paused,
      time: this.loop.simTime,
      // The PHASE of the cycle, not the current hour: `?tod=` is defined at sim
      // time zero, so combined with the `time` above this reproduces the exact
      // sky even when the loop is running. See `Atmosphere.setTimeOfDay`.
      tod: this.atmosphere.todParam,
      hud: this.hud.visible,
      panel: this.panel.visible,
      map: this.worldMap.visible,
      wireframe: this.renderer.wireframeEnabled,
    };
  }

  /** Shareable URL reproducing exactly what is on screen. */
  currentUrl(): string {
    return `${location.origin}${location.pathname}${serializeParams(this.currentParams())}`;
  }

  /** Exposed for the screenshot harness and for manual debugging. */
  hudText(): string {
    return this.hud.render();
  }

  /**
   * Machine-readable state for `npm run soak`.
   *
   * The five-minute flat-heap budget cannot be checked by a human staring at
   * the HUD, so everything the soak test asserts on is exposed here in one
   * plain object that survives `page.evaluate`'s structured clone.
   */
  perfSnapshot(): Record<string, number | boolean> {
    const frame = this.frameTimer.stats;
    const render = this.renderer.stats();
    const chunks = this.streamer.stats();
    const camera = this.rig.position;
    const sky = this.atmosphere.current;

    return {
      nowMs: performance.now(),
      /**
       * Phase 10. THE GUARD AGAINST A CLOCK THAT STOPPED.
       *
       * A frozen sun and a running sun produce byte-identical evidence in every
       * other lighting measurement this phase adds: shadow counts, star counts
       * and material counts all pass perfectly on a world stuck at 09:30. The
       * soak asserts the time of day actually SPANS a range across the flight,
       * which is the only observation that can tell the two apart.
       */
      todHours: sky.todHours,
      sunElevationDeg: sky.sun.elevationDeg,
      moonElevationDeg: sky.moon.elevationDeg,
      sunIntensity: sky.sun.intensity,
      starVisibility: sky.starVisibility,
      skyDrawCalls: this.skyDrawCalls,
      starDrawCalls: this.starDrawCalls,
      moonDrawCalls: this.moonDrawCalls,
      heapMb: readHeapMb() ?? -1,
      fps: frame.fps,
      avgFrameMs: frame.avgMs,
      windowMaxFrameMs: frame.maxMs,
      peakFrameMs: this.frameTimer.peakMs,
      spikes: this.frameTimer.totalSpikes,
      frames: this.frameTimer.totalFrames,
      drawCalls: render.drawCalls,
      triangles: render.triangles,
      geometries: render.geometries,
      textures: render.textures,
      liveChunks: chunks.live,
      cachedChunks: chunks.cached,
      queuedChunks: chunks.queued,
      inFlightChunks: chunks.inFlight,
      generatedChunks: chunks.generated,
      cancelledChunkRequests: chunks.cancelledRequests,
      evictedChunks: chunks.evicted,
      chunkBytes: chunks.bytes,
      // From the chunk payloads, not from the renderer: these are what the
      // world costs regardless of which GPU (or software rasteriser) draws it,
      // which is why the soak can budget them and cannot budget frame time.
      chunkTriangles: chunks.triangles,
      chunkVertices: chunks.vertices,
      // Phase 3a. `waterNodes` is what was generated and resident;
      // `waterDrawCalls` is what actually reached the rasteriser. The soak
      // needs both -- a world full of sea that is never on screen would pass
      // every water check ever written against the first number alone.
      waterNodes: chunks.waterNodes,
      waterTriangles: chunks.waterTriangles,
      waterDrawCalls: this.waterDrawCalls,
      // Phase 3b, mirroring the pair above: what was carved and resident, and
      // what actually reached the rasteriser.
      riverNodes: chunks.riverNodes,
      riverVertices: chunks.riverVertices,
      riverDrawCalls: this.riverDrawCalls,
      // Phase 4a, mirroring the pair above for exactly the same reason.
      roadNodes: chunks.roadNodes,
      roadVertices: chunks.roadVertices,
      roadDrawCalls: this.roadDrawCalls,
      // Phase 4b, the Sector tier, mirroring the trio above once more.
      streetNodes: chunks.streetNodes,
      streetVertices: chunks.streetVertices,
      streetDrawCalls: this.streetDrawCalls,
      // Phase 5. `deckNodes` is deck geometry that is resident, `deckDrawCalls`
      // what reached the rasteriser, and `bridgeVertices` how much of it is
      // standing clear of the ground -- the last being the only evidence that a
      // road crossing a river became a bridge rather than stopping at the bank.
      deckNodes: chunks.deckNodes,
      deckTriangles: chunks.deckTriangles,
      bridgeVertices: chunks.bridgeVertices,
      bridgeNodes: chunks.bridgeNodes,
      deckDrawCalls: this.deckDrawCalls,
      // Phase 6, and the same trio once more. `buildings` is what is resident,
      // `buildingDrawCalls` what reached the rasteriser, and `buildingsLevel`
      // the only one that says the houses are standing on ground a village
      // levelled rather than merely standing somewhere.
      buildingNodes: chunks.buildingNodes,
      buildings: chunks.buildings,
      buildingsMeasured: chunks.buildingsMeasured,
      buildingsLevel: chunks.buildingsLevel,
      buildingTriangles: chunks.buildingTriangles,
      buildingsSeen: chunks.buildingsSeen,
      buildingDrawCalls: this.buildingDrawCalls,
      wallNodes: chunks.wallNodes,
      walls: chunks.walls,
      wallTriangles: chunks.wallTriangles,
      wallsSeen: chunks.wallsSeen,
      citiesSeen: chunks.citiesSeen,
      politiesSeen: chunks.politiesSeen,
      culturesSeen: chunks.culturesSeen,
      wallDrawCalls: this.wallDrawCalls,
      interiorsEntered: this.interiors.interiorsEntered,
      // Phase 7a. Same trio once more: residency, rasteriser, seating.
      propNodes: chunks.propNodes,
      props: chunks.props,
      propsMeasured: chunks.propsMeasured,
      propsSeated: chunks.propsSeated,
      propTriangles: chunks.propTriangles,
      propsSeen: chunks.propsSeen,
      propDrawCalls: this.propDrawCalls,
      layoutSeenRing: chunks.layoutSeenRing,
      layoutSeenLinear: chunks.layoutSeenLinear,
      layoutSeenGrid: chunks.layoutSeenGrid,
      layoutSeenHilltop: chunks.layoutSeenHilltop,
      layoutSeenCity: chunks.layoutSeenCity,
      buildingsSeenCottage: chunks.buildingsSeenCottage,
      buildingsSeenBarn: chunks.buildingsSeenBarn,
      buildingsSeenHall: chunks.buildingsSeenHall,
      buildingsSeenTownhouse: chunks.buildingsSeenTownhouse,
      buildingsSeenGuildhall: chunks.buildingsSeenGuildhall,
      buildingsSeenWarehouse: chunks.buildingsSeenWarehouse,
      buildingsSeenKeep: chunks.buildingsSeenKeep,
      buildingsSeenCathedral: chunks.buildingsSeenCathedral,
      buildingsSeenTownhall: chunks.buildingsSeenTownhall,
      buildingsSeenGatehouse: chunks.buildingsSeenGatehouse,
      buildingsSeenSimplified: chunks.buildingsSeenSimplified,
      propsSeenPine: chunks.propsSeenPine,
      propsSeenBroadleaf: chunks.propsSeenBroadleaf,
      propsSeenBushRound: chunks.propsSeenBushRound,
      propsSeenBushTall: chunks.propsSeenBushTall,
      propsSeenYard: chunks.propsSeenYard,
      // Phase 9a/9b. NPCs are a main-thread overlay, not chunk content, so
      // these come from `NpcOverlay` rather than the streamer's stats --
      // see its header for why `npcsMoved`/`npcsArrived` are CUMULATIVE
      // rather than instantaneous (the same reasoning `bridgeNodes` uses).
      npcsRostered: this.npcs.npcsRostered,
      npcsVisible: this.npcs.npcsVisible,
      npcDrawCalls: this.npcDrawCalls,
      npcsMoved: this.npcs.npcsMoved,
      npcsArrived: this.npcs.npcsArrived,
      workers: chunks.workers,
      // Phase 2b. The quadtree's whole job is bounding these two.
      selectedNodes: chunks.selected,
      rootLod: chunks.rootLod,
      viewDistance: chunks.viewDistance,
      cameraX: camera.x,
      cameraY: camera.y,
      cameraZ: camera.z,
      cameraPitch: this.rig.look.pitch,
      settled: chunks.settled,
    };
  }

  /** Selected nodes per quadtree level, index = lod. For the soak's report. */
  lodCounts(): number[] {
    return this.streamer.stats().lodCounts;
  }

  /**
   * Aim the camera, in degrees. The soak uses this to fly one leg near the
   * horizon: draw calls and triangle counts measured from a near-nadir view are
   * a measurement of frustum culling, not of the world.
   */
  setLook(yaw: number, pitch: number): void {
    this.rig.setLook(yaw, pitch);
  }

  /**
   * Reposition / re-aim without a page reload. The screenshot harness uses this
   * to capture nearby views that share seed and sim time: the streamer keeps
   * its cache, and only the new fringe has to generate.
   *
   * Calls `streamer.update` synchronously so `worldSettled()` reflects the
   * new selection before the next animation frame.
   */
  seekCamera(
    pos: { x: number; y: number; z: number },
    look: { yaw: number; pitch: number },
    wireframe?: boolean,
  ): void {
    this.rig.setPosition(pos.x, pos.y, pos.z);
    this.rig.setLook(look.yaw, look.pitch);
    if (wireframe !== undefined) this.renderer.setWireframe(wireframe);
    this.streamer.update(this.rig.camera.position);
    this.interiors.update(pos.x, pos.z, this.params.walk);
  }

  /** Start a fresh worst-frame window, so warm-up hitches do not skew a soak run. */
  resetFrameStats(): void {
    this.frameTimer.resetPeak();
  }

  /** True when every chunk in range is resident. `window.__worldReady` waits on this too. */
  worldSettled(): boolean {
    return this.streamer.settled;
  }

  /**
   * Colours of the chunks in a square around a world position, as actually
   * resident. `null` where the chunk is not loaded. The soak test compares the
   * result before and after a round trip.
   */
  sampleChunkColors(worldX: number, worldZ: number, radius: number): (readonly number[] | null)[] {
    return this.streamer.sampleColors(ChunkStreamer.coordsAround(worldX, worldZ, radius));
  }

  /**
   * Hashes of the position buffers of the chunks in a square around a world
   * position, as actually resident. `null` where the chunk is not loaded.
   *
   * The soak test compares this before and after a round trip. Byte-identical
   * geometry after an unload and a regeneration is the strongest statement of
   * RULE 2 available without shipping the buffers out of the page.
   */
  sampleChunkGeometry(worldX: number, worldZ: number, radius: number): (number | null)[] {
    return this.streamer.sampleGeometryHashes(ChunkStreamer.coordsAround(worldX, worldZ, radius));
  }

  /**
   * Water triangles in the chunks around a world position, as actually
   * resident. `null` where the chunk is not loaded.
   *
   * The soak asserts this is non-zero somewhere in the round-tripped square, so
   * that "geometry came back byte-identical" is a statement about the sea and
   * not only about the ground under it.
   */
  sampleChunkWater(worldX: number, worldZ: number, radius: number): (number | null)[] {
    return this.streamer.sampleWaterTriangles(ChunkStreamer.coordsAround(worldX, worldZ, radius));
  }

  /**
   * River-carved vertices in the chunks around a world position, as actually
   * resident. `null` where the chunk is not loaded.
   *
   * The soak asserts this is non-zero somewhere in the round-tripped square, so
   * that "geometry came back byte-identical" is a statement about the river as
   * well as about the ground and the sea.
   */
  sampleChunkRivers(worldX: number, worldZ: number, radius: number): (number | null)[] {
    return this.streamer.sampleRiverVertices(ChunkStreamer.coordsAround(worldX, worldZ, radius));
  }

  /**
   * Road-surfaced vertices in the chunks around a world position, as actually
   * resident. `null` where the chunk is not loaded.
   *
   * The Phase 4a counterpart of `sampleChunkRivers`, and needed for the same
   * reason: grading and surfacing move and recolour vertices the geometry hash
   * already covers, but only if the round-tripped square had a road in it.
   */
  sampleChunkRoads(worldX: number, worldZ: number, radius: number): (number | null)[] {
    return this.streamer.sampleRoadVertices(ChunkStreamer.coordsAround(worldX, worldZ, radius));
  }

  /**
   * Street-surfaced vertices in the chunks around a world position, as actually
   * resident. `null` where the chunk is not loaded.
   *
   * The Phase 4b counterpart, and the narrowest of the four: the round-tripped
   * square has to be inside a village, not merely near a road.
   */
  sampleChunkStreets(worldX: number, worldZ: number, radius: number): (number | null)[] {
    return this.streamer.sampleStreetVertices(ChunkStreamer.coordsAround(worldX, worldZ, radius));
  }

  /**
   * Deck triangles in the chunks around a world position, as actually resident.
   * `null` where the chunk is not loaded.
   *
   * The Phase 5 counterpart, and the water one rather than the road one: the
   * geometry hash now folds `deckPositions` in, so this is what says the
   * round-tripped square had a carriageway in it at all.
   */
  sampleChunkDecks(worldX: number, worldZ: number, radius: number): (number | null)[] {
    return this.streamer.sampleDeckTriangles(ChunkStreamer.coordsAround(worldX, worldZ, radius));
  }

  /**
   * Buildings in the chunks around a world position, as actually resident.
   * `null` where the chunk is not loaded.
   *
   * The Phase 6 counterpart, and the narrowest yet: the round-tripped square has
   * to contain a building, not merely be inside a village.
   */
  sampleChunkBuildings(worldX: number, worldZ: number, radius: number): (number | null)[] {
    return this.streamer.sampleBuildings(ChunkStreamer.coordsAround(worldX, worldZ, radius));
  }

  /**
   * Props in the chunks around a world position, as actually resident.
   * `null` where the chunk is not loaded.
   *
   * The Phase 7a counterpart: the round-tripped square has to contain vegetation,
   * not merely pass over bare ground whose empty prop buffers hash identically.
   */
  sampleChunkProps(worldX: number, worldZ: number, radius: number): (number | null)[] {
    return this.streamer.sampleProps(ChunkStreamer.coordsAround(worldX, worldZ, radius));
  }

  /** Ground height at a world position, from the main thread. For debugging parity. */
  groundHeight(worldX: number, worldZ: number): number {
    return sampleHeight(worldX, worldZ, this.params.seedHash);
  }

  /**
   * Move the camera straight to a world (x, z), used by the travel map's
   * click-a-settlement teleport. Sets `rig` position only -- both render
   * modes read off it (`PlayerController.update` reads `rig.camera.position`
   * directly), so one call correctly relocates a flying OR a walking player,
   * the same way the constructor's own start-position logic does not special
   * case `?walk=`. Chunk streaming and interiors need no extra nudge: both
   * already run unconditionally every frame in `renderFrame`.
   */
  private teleportTo(x: number, z: number): void {
    const y = sampleHeight(x, z, this.params.seedHash) + TELEPORT_HEIGHT_OFFSET;
    this.rig.setPosition(x, y, z);
  }

  private renderFrame(wallDt: number): void {
    // The travel map freezes movement while open -- it builds its field once
    // around the position at the moment it opened (see `TravelMap.openAt`),
    // so a camera that kept drifting under it would silently invalidate the
    // "you are here" marker and the distances in its settlement list.
    if (!this.travelMap.open) {
      if (this.player === null) this.rig.update(wallDt);
      else this.player.update(wallDt);
    }
    // THE FLIGHT DOES NOT START UNTIL THE WORLD IS READY, and that is a Phase 4b
    // correctness fix rather than a tidy-up. The autopilot used to advance from
    // the first rendered frame, so it was already moving while the world
    // streamed in -- on this container that is 8-20 seconds at 45 m/s, i.e. the
    // soak's flight began anywhere from 400 m to 900 m downrange of `?pos=`, by
    // an amount that depends on how loaded the machine is.
    //
    // Everything the soak says "at the start" is a claim about the chunks around
    // wherever the camera had drifted to, so choosing a start that crosses a
    // river, a road and a village was not reproducible: Phase 4b's chosen start
    // measured street 10/25 in the square it names and 0/25 in the square the
    // run actually sampled, 760 m away. Holding the flight until
    // `__worldReady` makes the origin exactly `?pos=`, on every machine.
    if (
      this.autopilot.active &&
      window.__worldReady === true &&
      window.__flightReleased !== false
    ) {
      const p = this.rig.position;
      this.rig.setPosition(this.autopilot.advance(wallDt, p.x), p.y, p.z);
    }
    this.streamer.update(this.rig.camera.position);
    this.interiors.update(
      this.rig.camera.position.x,
      this.rig.camera.position.z,
      this.params.walk,
    );
    // Residency and instance upload only -- simulation already advanced in
    // the fixed update above. See `NpcOverlay`'s header.
    this.npcs.update(this.rig.camera.position.x, this.rig.camera.position.z, this.params.npc);
    // A couple of milliseconds of budget: enough to finish a 256x256 build
    // over a few dozen frames, never enough to show up in the frame budget.
    // A no-op call (hidden, or the current view already fully built) is a
    // handful of comparisons -- see `WorldMap.update`.
    this.worldMap.update(this.rig.camera.position.x, this.rig.camera.position.z, 2);
    this.travelMap.update();
    // The sky reads SIM time, not wall time, so `?freeze=1` holds the sun and
    // every canonical screenshot keeps a fixed hour. It follows the camera
    // because a directional light's shadow camera has to be near what it
    // shadows -- see `atmosphere.ts`.
    this.atmosphere.update(this.loop.simTime, this.rig.camera.position);
    this.frameTimer.sample(wallDt);
    resetWaterDraws();
    resetRiverDraws();
    resetRoadDraws();
    resetStreetDraws();
    resetDeckDraws();
    resetBuildingDraws();
    resetWallDraws();
    resetPropDraws();
    resetNpcDraws();
    resetSkyDraws();
    this.renderer.render(this.scene, this.rig.camera);
    // Phase 10. Read immediately after the render, like every counter above:
    // `skyDrawCalls` proves the dome reached the rasteriser rather than merely
    // existing, and `starDrawCalls` proves the flight actually reached a night.
    this.skyDrawCalls = skyDrawsSinceReset();
    this.starDrawCalls = starDrawsSinceReset();
    this.moonDrawCalls = moonDrawsSinceReset();
    this.waterDrawCalls = waterDrawsSinceReset();
    this.riverDrawCalls = riverDrawsSinceReset();
    this.roadDrawCalls = roadDrawsSinceReset();
    this.streetDrawCalls = streetDrawsSinceReset();
    this.deckDrawCalls = deckDrawsSinceReset();
    this.buildingDrawCalls = buildingDrawsSinceReset();
    this.wallDrawCalls = wallDrawsSinceReset();
    this.propDrawCalls = propDrawsSinceReset();
    this.npcDrawCalls = npcDrawsSinceReset();
    this.hud.update(wallDt);

    this.renderedFrames++;
    // Readiness now also waits for the world around the camera to be fully
    // streamed. Without that, a screenshot could catch a half-loaded world and
    // the byte-comparison harness would go flaky the moment Phase 1 landed.
    if (
      window.__worldReady !== true &&
      this.renderedFrames >= READY_FRAME_COUNT &&
      this.streamer.settled
    ) {
      window.__worldReady = true;
    }
  }

  private registerHudLines(): void {
    const hud = this.hud;

    hud.register('fps', () => this.frameTimer.stats.fps, HudOrder.frame);
    hud.register(
      'frame ms',
      () => {
        const { avgMs, maxMs, spikes } = this.frameTimer.stats;
        return `${avgMs.toFixed(1)} avg / ${maxMs.toFixed(1)} max / ${spikes} spikes`;
      },
      HudOrder.frame,
    );

    hud.register('draw calls', () => this.renderer.stats().drawCalls, HudOrder.render);
    hud.register('triangles', () => formatCount(this.renderer.stats().triangles), HudOrder.render);
    hud.register('programs', () => this.renderer.stats().programs, HudOrder.render);
    hud.register(
      'sky draws',
      () => `${this.skyDrawCalls} sky / ${this.starDrawCalls} stars / ${this.moonDrawCalls} moon`,
      HudOrder.render,
    );
    hud.register('water draws', () => this.waterDrawCalls, HudOrder.render);
    hud.register('river draws', () => this.riverDrawCalls, HudOrder.render);
    hud.register('road draws', () => this.roadDrawCalls, HudOrder.render);
    hud.register('street draws', () => this.streetDrawCalls, HudOrder.render);
    hud.register('deck draws', () => this.deckDrawCalls, HudOrder.render);
    hud.register('building draws', () => this.buildingDrawCalls, HudOrder.render);
    hud.register('wall draws', () => this.wallDrawCalls, HudOrder.render);
    hud.register('interiors entered', () => this.interiors.interiorsEntered, HudOrder.world);
    hud.register('prop draws', () => this.propDrawCalls, HudOrder.render);
    hud.register('npc draws', () => this.npcDrawCalls, HudOrder.render);
    hud.register(
      'npcs',
      () => `${this.npcs.npcsVisible}/${this.npcs.npcsRostered} moved ${this.npcs.npcsMoved} arrived ${this.npcs.npcsArrived}`,
      HudOrder.world,
    );

    hud.register(
      'js heap',
      () => {
        const mb = readHeapMb();
        return mb === null ? 'n/a' : `${mb.toFixed(1)} MB`;
      },
      HudOrder.memory,
    );

    // Phase 1 owns these two. Registering by the same label replaces the
    // provider, so the streamer supplies real values without editing this file.
    hud.register('chunks', () => 0, HudOrder.world);
    hud.register('worker queue', () => 0, HudOrder.workers);

    hud.register(
      'sim',
      () => `tick ${this.loop.tick} / t ${this.loop.simTime.toFixed(2)}s${this.loop.paused ? ' (paused)' : ''}`,
      HudOrder.world,
    );

    hud.register('seed', () => `${this.params.seed} (${this.params.seedHash})`, HudOrder.misc);
    hud.register(
      'camera',
      () => {
        const p = this.rig.position;
        return `${p.x.toFixed(1)}, ${p.y.toFixed(1)}, ${p.z.toFixed(1)}`;
      },
      HudOrder.misc,
    );
    hud.register('backend', () => this.renderer.backend, HudOrder.misc);
    hud.register(
      'polity',
      () => this.worldMap.labelAt(this.rig.camera.position.x, this.rig.camera.position.z),
      HudOrder.world,
    );
    hud.register(
      'map',
      () => (this.travelMap.open ? 'travel map open (Esc closes)' : 'M: minimap  N: travel map'),
      HudOrder.world,
    );
  }

  private buildPanel(): void {
    const sim = this.panel.folder('Simulation');
    sim.addToggle(
      'paused',
      () => this.loop.paused,
      (value) => (value ? this.loop.pause() : this.loop.resume()),
    );
    sim.addButton('step 1 tick', () => this.loop.step(1));
    sim.addButton('step 60 ticks', () => this.loop.step(60));

    const render = this.panel.folder('Render');
    render.addToggle(
      'wireframe',
      () => this.renderer.wireframeEnabled,
      (value) => this.renderer.setWireframe(value),
    );
    render.addToggle(
      'hud',
      () => this.hud.visible,
      (value) => this.hud.setVisible(value),
    );
    // Exposure lives here rather than in the Sky folder: it is a property of
    // the output pipeline, not of the sky, and keeping it here means
    // `Atmosphere` never needs a reference to the renderer.
    render.addNumber(
      'exposure',
      () => this.renderer.toneMappingExposure,
      (value) => this.renderer.setToneMappingExposure(value),
      { min: 0.2, max: 3, step: 0.05 },
    );

    const map = this.panel.folder('World map');
    map.addToggle(
      'visible',
      () => this.worldMap.visible,
      (value) => this.worldMap.setVisible(value),
    );
    map.addNumber(
      'metres/px',
      () => this.worldMap.metresPerPixel,
      (value) => this.worldMap.setScale(value),
      { min: 32, max: 512, step: 32 },
    );
    map.addButton('open travel map (N)', () => {
      document.exitPointerLock();
      const p = this.rig.position;
      this.travelMap.toggle(p.x, p.z);
    });

    const share = this.panel.folder('Share');
    share.addButton('copy link to this view', () => {
      const url = this.currentUrl();
      void navigator.clipboard?.writeText(url);
      console.info(url);
    });
    share.addButton('log HUD text', () => console.info(this.hudText()));
  }

  private readonly onResize = (): void => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    this.renderer.resize(width, height, window.devicePixelRatio);
    this.rig.setAspect(width / Math.max(height, 1));
  };

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    switch (event.code) {
      case 'Space':
        event.preventDefault();
        this.loop.togglePaused();
        break;
      case 'Period':
        this.loop.step(1);
        break;
      case 'KeyF':
        this.renderer.setWireframe(!this.renderer.wireframeEnabled);
        break;
      case 'KeyH':
        this.hud.setVisible(!this.hud.visible);
        break;
      case 'KeyM':
        this.worldMap.setVisible(!this.worldMap.visible);
        break;
      case 'KeyN': {
        // A locked pointer has no visible cursor, so a click on the travel
        // map's canvas or list would land nowhere. Harmless to call when
        // already unlocked -- `exitPointerLock` is a no-op in that case.
        document.exitPointerLock();
        const p = this.rig.position;
        this.travelMap.toggle(p.x, p.z);
        break;
      }
      default:
        break;
    }
  };
}
